import React, {useCallback, useEffect} from 'react';
import {observer} from 'mobx-react';
import ReactQuill, {Quill} from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import s from '../../s';

const Block = Quill.import('blots/block');
const Inline = Quill.import('blots/inline');

class ColorStyle extends Inline {
    static create(value) {
        const node = super.create();

        node.style.color = value;

        return node;
    }

    static formats(node) {
        return node.style.color;
    }
}

class BackgroundStyle extends Inline {
    static create(value) {
        const node = super.create();

        node.style.backgroundColor = value;

        return node;
    }

    static formats(node) {
        return node.style.backgroundColor;
    }
}

class IndentStyle extends Block {
    static create(value) {
        const node = super.create();

        value = value > 0 ? value : 0;
        node.style.marginLeft = `${value * 0.75}em`;

        return node;
    }

    static formats(node) {
        const margin = node.style.marginLeft || '0';

        return parseFloat(margin) / 0.75;
    }
}

class SizeStyle extends Inline {
    static create(value) {
        const node = super.create();
        const sizeMap = {
            small: '0.75em',
            normal: '1em',
            large: '1.5em',
            huge: '2em',
        };

        node.style.fontSize = sizeMap[value] || value;

        return node;
    }

    static formats(node) {
        return node.style.fontSize;
    }
}

class FontStyle extends Inline {
    static create(value) {
        const node = super.create();

        node.style.fontFamily = value;

        return node;
    }

    static formats(node) {
        return node.style.fontFamily;
    }
}

class AlignStyle extends Block {
    static create(value) {
        const node = super.create();

        node.style.textAlign = value;

        return node;
    }

    static formats(node) {
        return node.style.textAlign || 'left';
    }
}

AlignStyle.blotName = 'align';
AlignStyle.tagName = 'p';
Quill.register(AlignStyle, true);

FontStyle.blotName = 'font';
FontStyle.tagName = 'span';
Quill.register(FontStyle, true);

SizeStyle.blotName = 'size';
SizeStyle.tagName = 'span';
Quill.register(SizeStyle, true);

IndentStyle.blotName = 'indent';
IndentStyle.tagName = 'p';
Quill.register(IndentStyle, true);

ColorStyle.blotName = 'color';
ColorStyle.tagName = 'span';
Quill.register(ColorStyle, true);

BackgroundStyle.blotName = 'background';
BackgroundStyle.tagName = 'span';
Quill.register(BackgroundStyle, true);

const modules = {
    toolbar: [[{font: ['serif', 'Roboto']}], [{size: ['small', false, 'large', 'huge']}], ['bold', 'italic', 'underline', 'strike'], [{color: []}, {background: []}], [{list: 'ordered'}, {list: 'bullet'}], [{indent: '-1'}, {indent: '+1'}], [{align: []}], ['clean']],
};

const formats = ['header', 'font', 'size', 'bold', 'italic', 'underline', 'strike', 'color', 'background', 'list', 'bullet', 'indent', 'align'];

export const EPEditor = observer(() => {
    useEffect(() => {
        setContainerStyle();
    }, []);

    const setContainerStyle = () => {
        const container = document.querySelector('.ql-container') as HTMLElement;

        container.style.width = s.pcs.css.ep_width;
        container.style.height = s.pcs.css.ep_height;
    };

    const setValue = useCallback((value: string) => {
        s.ps.RichText = value;
        s.ui.doRender = true;
        s.ui.update_pricing = true;
        s.pulse();
    }, []);

    return (
        <ReactQuill
            theme="snow"
            onChange={setValue}
            modules={modules}
            formats={formats}
        />
    );
});
